import Component from 'vue-class-component';
import { Mixins, Prop } from 'vue-property-decorator';
import Moment from 'moment';
import KEYS from '@/values/keys';
import CalendarMixin from '@/mixins/common/calendar.mixin';
import { _SCHOOL_CALENDAR_STORE, SCHOOL_DATE_TYPE } from '@/tools/values/school-calendar.values';
import { WEEK_DAYS } from '@/values/general.values';
import EdSchoolCalendar from '@/core/components/ed-school-calendar/ed-school-calendar.component.vue';
import EdSubjectDayContentComponent from '../ed-subject-day-content/ed-subject-day-content.component.vue';
import EdSubjectDayOptionsComponent from '../ed-subject-day-options/ed-subject-day-options.component.vue';
import { ALL_CONTENT_ROUTES } from '@/app/private/my-content/views/all/all.router';
import { BUILDER_EVENT_TYPES, BUILDER_CALENDAR_TYPES, BUILDER_RULES, BUILDER_DAY_OPTIONS, DAYS_OF_WEEK } from '@/courses/values/courses.values';
import { ISchoolDayCalendarEventItem } from '@/tools/interfaces/school-day-calendar-event-item.interface';
import { SubjectBuilderLessonType, SubjectBuilderMoveType } from '@/courses/types/subject-builder.types';
import { ISubjectBuilderMoveOptions } from '@/courses/interfaces/subject-builder.interfaces';
import { Status } from '@/models/status.model';
import { IParamsCreateComponent } from '@/courses/interfaces/params-create-component.interface';
import { BuilderLesson, BuilderSchoolDaySlot } from '@/app/private/subjects/models/builder.model';
import EdMoveLessonsModal from '../ed-move-lessons-modal/ed-move-lessons-modal.component.vue';

@Component({
  components: {
    EdSchoolCalendar,
    EdSubjectDayContentComponent,
    EdSubjectDayOptionsComponent
  }
})
export default class EdSubjectCalendar extends Mixins(CalendarMixin) {

  /* Props */
  @Prop({ default: 'CALENDAR' }) calendarType: 'CALENDAR' | 'LESSONS';
  @Prop({ default: () => new Status() }) status: Status;
  @Prop({ default: '' }) coverImage: string;
  @Prop({ default: () => [] }) lessons: any[];
  @Prop({ default: () => [] }) specialDays: any[];
  @Prop({ default: () => [] }) plainWorkingDays: any[];
  @Prop({ default: () => [] }) enabledWorkingDays: any[];
  @Prop({ default: '' }) startDateCalendar: string;
  @Prop({ default: '' }) endDateCalendar: string;
  @Prop({ default: () => {} }) schoolDay: any;
  @Prop() schoolDaysSlotsMap: Map<string, BuilderSchoolDaySlot>;
  @Prop() allLessonsMap : Map<number, BuilderLesson>;
  @Prop({ default: false }) showDuration: boolean;
  @Prop({ default: 0 }) existFunLessons: number;
  @Prop({ default: () => {} }) subject: number;
  @Prop({ default: () => [] }) additionalLessons: any[];
  @Prop({ default: true }) hasWorkingDaysIcon: boolean;
  @Prop({ default: true }) canLessonsBeMoved: boolean;
  @Prop({ default: true }) canLessonsOpened: boolean;
  @Prop({ default: false }) canMoveLessons: boolean;
  @Prop({ default: () => [] }) objectsTagged: any[];
  @Prop({ default: '' }) automaticFolderType: string;
  @Prop({ default: () => [] }) componentsSelected: IParamsCreateComponent[];
  @Prop() templateIdFromQuickSubject: any;
  @Prop() builderId: any;
  @Prop({ default: true }) readonly showEvents: boolean;

  /* Data */
  public config: {
    showSidebar: boolean,
    eventHeight: number,
    showTabs: boolean,
    showToday: boolean,
    showNavigator: boolean,
    showWorkingDays: boolean,
  } = {
    showSidebar: false,
    eventHeight: 20,
    showTabs: false,
    showToday: false,
    showNavigator: false,
    showWorkingDays: false,
  }; 
  public saving: boolean = false;
  public calendarTypes: any = BUILDER_CALENDAR_TYPES;
  public eventTypes: any = BUILDER_EVENT_TYPES;
  public isEditionMode = false;
  public activateBottomNavigation = false;
  public srcLessonSelected: any = null;
  public moveType: SubjectBuilderMoveType = null;
  public lessonTypeSelected: SubjectBuilderLessonType = null;
  public changeSelected = null;
  public prevLessonDates = null;
  public availableTargetLessons: any = [];
  
  public targetLessonSelected: any = null;
  /* Control variable for move lessons rule (V-select) */
  public targetsLessonFilteredToSelect: any = [];
  public targetLessonId: any = null;

  /* Computed */
  get targetLessons(){
    let targets = this.targetsLessonFilteredToSelect.map(l => { return { text: l.name, value: l.id } });
    this.targetLessonId = targets[0].value;
    return targets;
  }

  get isCalendarStep() {
    return this.calendarType == this.calendarTypes.CALENDAR;
  }

  get isLessonStep() {
    return this.calendarType == this.calendarTypes.LESSONS;
  }

  get workingDaysLabel () {
    return this.$t('courses.subjectBuilder.subjectTeachingDays');
  }

  get templateLessons() {
    if (this.isLessonStep) {
      return this.lessons;
    }
    return;
  }

  get funSpecialDays() {
    return this.specialDays.filter(el => el.status == BUILDER_EVENT_TYPES.FUN) || [];
  }

  get calendarObjects(): ISchoolDayCalendarEventItem[] {
    let addFunDays = [];
    if(this.isLessonStep && this.existFunLessons < this.funSpecialDays?.length) {
      this.funSpecialDays?.forEach(fl => {
        const startFunDate: Date = new Date(fl.startDate);
        if(this.templateLessons && !this.templateLessons.find(tl => {
            const startDate = tl.startSchoolDate ? tl.startSchoolDate : tl.schoolDate;
            const lessonFunDate: Date = new Date(startDate);
            return (startFunDate.getTime() === lessonFunDate.getTime());
          })) {
          addFunDays.push(fl)
        } 
      })
    }
    return this.isCalendarStep ? this.getFunCalendarFormat(this.funSpecialDays) : this.getCalendarFormat(this.templateLessons).concat(this.getFunCalendarFormat(addFunDays))
  }
  
  get templateId (): number {
    return Number(this.$route.params.id);
  }

  get workingDays() {
    let days = [];
    for (let dayOfWeek of WEEK_DAYS) {
      days.push({
        label: dayOfWeek,
        checked: this.plainWorkingDays?.includes(dayOfWeek.toUpperCase())
      });
    }
    return days;
  }

  get organizationId() {
    let institutions: any[] = this.$store.state.ToolsStore.institutionInfo;
    return institutions.length > 0 ? institutions[0].organizationId : null;
  }

  get weekdays() {
    return DAYS_OF_WEEK.map((_, i) => i); //Show al week days
  }

  get startYear() : number {
    let startYear = 0;
    let startMonth = Number(Moment(this.schoolDay?.startDate).format('MM'));
    if(this.schoolDay){
      startYear = parseInt(this.schoolDay.startDate?.substr(0, 4));
    }
    if(startMonth < 8) startYear -= 1;
    return startYear;
  }

  get moveLabels(): ISubjectBuilderMoveOptions {
    const isToNotContent = this.targetLessonSelected?.rootType !== 'CONTENT';
    const optionSublabelB = isToNotContent ? 'optionSublabelBToNotContent' : 'optionSublabelB';

    return {
      optionA: {
        label: this.getOptionLabel('optionLabelA'),
        sublabel: this.getOptionLabel('optionSublabelA'),
      },
      optionB: {
        label: this.getOptionLabel('optionLabelB'),
        sublabel: this.getOptionLabel(optionSublabelB),
      },
      optionC: {
        label: this.getOptionLabel('optionLabelC')
      }
    }
  }

  get calendarState() {
    return {
      templateId: this.templateId, 
      schoolDay: this.schoolDay, 
      workingDays: this.plainWorkingDays,
      specialDays: this.specialDays,
      subject: this.subject,
      lessons: this.lessons.filter(el => !el.labelSections),
      objectsTagged: this.objectsTagged,
      automaticFolderType: this.automaticFolderType,
      componentsSelected: this.componentsSelected
    }
  }

  get draggableDates(): string[] {
    const currentDate = Moment();
    return this.schoolDay?.dates?.filter((date: string) => Moment(date).isAfter(currentDate)
      && !this.funSpecialDays?.find(fun => fun.startDate === date)
    ) || [];
  }

  
  /**
   * Override CalendarMixin.getIsSchoolDay to optimize
   */
  getIsSchoolDay(date) {
    let slot = this.schoolDaysSlotsMap.get(date);
    if(slot) 
      return slot.totalTime === null || slot.totalTime > 0;
    return false;
  }

  getDayTooltip(props) {
    let tooltip = this.getSchoolDay(props.date, props.schoolDays).day + 1
    if(this.showDuration) {
      let slot = this.schoolDaysSlotsMap.get(props.date);
      if(slot.totalTime)
        return `${tooltip} (${slot.totalTime} ${this.$t('courses.createCourse.minutes')})`;
    }
    return tooltip;
  }

  isWithinSchoolDays(date: string) {
    const start = Moment(this.schoolDay.startDate, 'YYYY-MM-DD');
    const end = Moment(this.schoolDay.endDate, 'YYYY-MM-DD');
    const currentDate = Moment(date, 'YYYY-MM-DD');
    if (currentDate.isBetween(start, end, 'day', '[]')) 
      return true;
    return false;
  }

  getOptionLabel(labelOption: string): string {
    return this.$i18n.t(`courses.subjectBuilder.movements.${this.moveType}.${labelOption}`, { 
      from: this.srcLessonSelected?.name,
      to: this.targetLessonSelected?.name,
      dateFrom: Moment(this.prevLessonDates?.schoolDate).format('MMM Do')
    }).toString()
  }

  getCalendarFormat(sections): ISchoolDayCalendarEventItem[] {
    let filtered: ISchoolDayCalendarEventItem[] = [];

    sections.forEach(el => {
      const startDate = el.startSchoolDate ? el.startSchoolDate : el.schoolDate;
      if (el.fixedSection === 'LESSON' && startDate) {
        let label = 'contentLesson';
        if(el.rootType == BUILDER_EVENT_TYPES.FILLER) label = 'fillerLesson';
        else if(el.rootType == BUILDER_EVENT_TYPES.FUN) label = 'funLesson';
        filtered.push({
          category: el.rootType,
          className: `event-${el.rootType.toLocaleLowerCase()}`,
          color: null,
          end: el.endSchoolDate ? el.endSchoolDate : el.schoolDate,
          id: el.id,
          label: `${this.$t(`courses.subjectBuilder.${label}`)} ${el.fixedSequence}`,
          name: el.createAsNew ? `${el.name} ${el.fixedSequence}` : el.name,
          official: false,
          start:  startDate,
          status: undefined,
          timed: false,
          parentId: el.parentId,
          type: el.rootType,
          subType: el.fixedSection,
        });
      } else if (el.sections?.length > 0) { 
        filtered = [...filtered, ...this.getCalendarFormat(el.sections)];
      }
    });

    return filtered;
  }

  getFunCalendarFormat(lessons): ISchoolDayCalendarEventItem[] {
    let filtered: ISchoolDayCalendarEventItem[] = [];

    lessons.forEach(el => {
      filtered.push({
        category: el.status,
        className: `event-${el.status.toLocaleLowerCase()}`,
        color: null,
        end: el.endDate,
        id: el.holidayPlanId,
        label: `${this.$i18n.t('courses.subjectBuilder.funDay')}`,
        name: `${this.$i18n.t('courses.subjectBuilder.funDay')}`,
        official: false,
        start: el.startDate,
        status: undefined,
        timed: false,
        type: el.status,
      });
    })

    return filtered;
  }

  goToPresentDay() {
    setTimeout(() => {
      let present: any = document.querySelector('.v-calendar-weekly__day.v-present');
      if(present) {
        let top = present.offsetTop - 300;
        document.getElementById('subjectBuilder').scrollTop = top;
      }
    }, 500);
  }

  
  showDayContentModal (id, date = null) {    
    const lesson = this.templateLessons.find(el => el.id == id);

    this.$EdModal.createModal(
      EdSubjectDayContentComponent,
      { 
        courseId: this.templateIdFromQuickSubject || this.templateId,
        builderId: this.builderId,
        lesson: lesson,
        objectsTagged: this.objectsTagged,
        date: date
      }
    );

  }

  showDayOptionsModal(date) {
    this.$EdModal.createModal(
      EdSubjectDayOptionsComponent,
      { 
        date: date,
        setSpecialDays: this.setSpecialDays,
        specialDays: this.specialDays,
        existFunLessons: this.existFunLessons,
        lessons: this.lessons,
        schoolDay: this.schoolDay
      }
    );
  }

  showMoveLessonModal() {
    this.$EdModal.createModal(EdMoveLessonsModal, 
      {
        targetLessonSelected: this.targetLessonSelected,
        srcLessonSelected: this.srcLessonSelected,
        prevLessonDates: this.prevLessonDates,
        moveType: this.moveType,
        targetLessons: this.targetLessons,
        targetLessonId: this.targetLessonId,
        moveLessons: this.moveLessons,
        onCancelMovement: this.onCancelMovement
      }
    );
  }

  goToTemplates() {
    this.$router.push({ name: ALL_CONTENT_ROUTES.TEMPLATES.NAME });
  }

  async saveTeachingDays(data) {
    this.$emit('onSaveTeachingDays', data);
  }

  async setSpecialDays(specialDays, rule, option = null, prevOption = null, date = '') {
    this.$emit('onSetSpecialDays', { specialDays, rule, option, prevOption, date });
  }

  isPastDate(date) {
    let todayDate = Moment(new Date(), 'DD-MM-YYYY');
    let pastDate = Moment(Moment(date), 'DD-MM-YYYY');
    return todayDate.diff(pastDate) > 0 ? false : true;
  }

  moveLessons(changeSelected) {
    const isFutureMoved = this.moveType === 'future';

    let rule, specialDays;
  
    switch (changeSelected) {
      case 1:
        rule = BUILDER_RULES.SWAP_LESSONS
        this.targetLessonSelected = this.targetsLessonFilteredToSelect.find(l => l.id === this.targetLessonId);
        break;
      case 2: 
        rule = isFutureMoved ? BUILDER_RULES.SHIFT_OF_THE_FEATURE : BUILDER_RULES.SHIFT_DAY_BACK;
        break;
      case 3: 
        rule = isFutureMoved ? BUILDER_RULES.SHIFT_OF_THE_FEATURE_WITHOUT_USE_LESSON : BUILDER_RULES.SHIFT_DAY_BACK_WITH_DAY_OFF;
        break;
      default: 
        rule = BUILDER_RULES.MOVE_LESSON_WHEREVER;
    }

    if (rule == BUILDER_RULES.SHIFT_DAY_BACK_WITH_DAY_OFF) {
      specialDays = this.specialDays.concat({
        endDate: this.prevLessonDates.schoolDate,
        holidayPlanId: null,
        name: this.$t('courses.subjectBuilder.dayOff'),
        official: false,
        startDate: this.prevLessonDates.schoolDate,
        status: BUILDER_DAY_OPTIONS.OBSERVED,
        addedByRule: true
      });
    } else {
      specialDays = this.specialDays;
    }

    rule = { 
      type: rule,
      lessonSourceId: this.srcLessonSelected.id,
      lessonTargetId: this.targetLessonSelected?.id,
      lessonDate: !this.targetLessonSelected?.id ? this.targetLessonSelected : undefined
    };

    const srcLessonFound = this.lessons.find(l => l.id === this.srcLessonSelected.id);
    srcLessonFound.schoolDate = this.prevLessonDates.schoolDate;

    this.setSpecialDays(specialDays, rule);
    this.initMoveValues();
  }

  initMoveValues() {
    this.changeSelected = null;
    this.srcLessonSelected = null;
    this.targetLessonSelected = null;
    this.prevLessonDates = null;
  }

  async refreshSubjectCalendar () {
    this.$emit('onRefreshSubjectCalendar');
  }

  onClickEvent(event) {
    this.showDayContentModal(event.id, event.start); 
  }

  refreshCalendar() {
    this.status.setLoading();
    (this.$refs.schoolCalendar as any)?.createCalendar();
    setTimeout(() => {
      this.status.setLoad();
    }, 100)
  }

  onCancelMovement() {
    this.$emit('onUpdateLessonDates', this.prevLessonDates);
    this.initMoveValues();
  }

  onLessonMoved({ event, from, to, startSchoolCalendarDay, endSchoolCalendarDay }) {
    const dateTo = (new Date(to)).getTime();
    const dateFrom = (new Date(from)).getTime();
    const _now = new Date();
    _now.setHours(0, 0, 0, 0);
    const now = _now.getTime();   //Set the current date to 00:00:00
    let srcLessonFound = null;
    this.targetsLessonFilteredToSelect = [];
    let lessonsInProgress = false;
    this.prevLessonDates = { //Dates to reset the data when canceling the movement
      id: event.id,
      schoolDate: Moment(event.lessonDates.start).format('YYYY-MM-DD'),
      endSchoolDate: Moment(event.lessonDates.end).format('YYYY-MM-DD'),
    };

    if (dateFrom <= now)                  // Prevent moving a past lesson
      return false;
    if (dateTo <= now)                    // Prevent moving a lesson to the past
      return false;

    let slot = this.schoolDaysSlotsMap.get(to);
    if (!slot || slot.totalTime === 0)                // Prevent moving to a day slot without time available
      return false;
    if(!slot.allowedTypes.includes(event.category))   // Prevent moving to a day slot with not allowed type
      return false;

    for (const lesson of this.lessons) {
      const startDate = lesson.startSchoolDate ? lesson.startSchoolDate : lesson.schoolDate;
      const endDate = lesson.endSchoolDate ? lesson.endSchoolDate : lesson.schoolDate;

      const lessonStartDate = new Date(startDate);
      const lessonEndDate = new Date(endDate);

      //find source lesson
      if (!srcLessonFound && lesson.id === event.id){ 
        srcLessonFound = lesson;
      }

      //find target lesson
      if (lessonStartDate.getTime() === dateTo && lessonStartDate.getTime() > now){
        this.targetsLessonFilteredToSelect.push(lesson);
      }
      //find lessons starting before the date in which it is wanted to move and ending after
      if (lessonStartDate.getTime() <= dateTo && lessonEndDate.getTime() >= dateTo){
        lessonsInProgress = true;
      }
    }

    if (!srcLessonFound) {
      return;
    }


    //If there are lessons in progress on target date, show a warning message and stop the movement
    if (lessonsInProgress && this.targetsLessonFilteredToSelect.length === 0) {
      this.$forceUpdate();
      this.onCancelMovement()
      this.$EdModal.createMessageModal({
        title: this.$i18n.t('courses.subjectBuilder.errors.movementForbiddenTitle'),
        body: this.$i18n.t('courses.subjectBuilder.errors.errorMovingLessonWithOutStartDate'),
        type: KEYS.EVENT_TYPES.WARNING
      });
      return;
    } 
       
    /* TODO: Check if the lesson to move fits between the destination date and the next fixed lesson, otherwise it should show an error message. 
     * Another option is to handle the FIXED as a request and try to put it on that date if it is available, otherwise move it to the next one 
     */

    this.srcLessonSelected = srcLessonFound;    
    if(this.targetsLessonFilteredToSelect.length === 0) {     // Move to a date with no lesson
      this.targetLessonSelected = to;
      this.moveLessons(this.changeSelected);
    } else {                                                  // Move to a date with lesson
      this.targetLessonSelected = this.targetsLessonFilteredToSelect[0];
      this.moveType = Moment(from).isBefore(Moment(to)) ? 'back' : 'future';
      this.$forceUpdate();
      this.showMoveLessonModal();
    }
  }

  movingLessons({ mouse, dragTime, dragEvent, startSchoolCalendarDay, endSchoolCalendarDay }) {
    let newStartTime;

    if(dragEvent.subType !== BUILDER_EVENT_TYPES.LESSON)              // Prevent moving Forbidden objects
      return false;
 
    newStartTime = mouse - dragTime;
    const newStartTimeFormat = Moment(newStartTime).format('YYYY-MM-DD');
    
    if (dragEvent.start <= new Date().getTime())                      //Prevent moving a past lesson
      return false;
    if (Moment(newStartTimeFormat).isSameOrBefore(Moment(), 'day'))   //Prevent moving lesson to a past day
      return false;
    let slot = this.schoolDaysSlotsMap.get(newStartTimeFormat);
    if (!slot || slot.totalTime === 0)                                //  Prevent moving to a day slot without time available
      return false
    if(!slot.allowedTypes.includes(dragEvent.category))               // Prevent moving to a day slot with not allowed type
      return false;
    
    let startCalendar = Moment(startSchoolCalendarDay);

    if(Moment(startSchoolCalendarDay).isBefore(Moment())) 
      startCalendar = Moment();

    if(Moment(newStartTimeFormat).isSameOrAfter(startCalendar) 
      && Moment(newStartTimeFormat).isSameOrBefore(Moment(endSchoolCalendarDay))) {
        return newStartTime
    }
    return false;
  }

  onWcCalendarDaySelection(customEvent: CustomEvent): void {
    const date = customEvent?.detail?.value;
    if (!date || Moment(date).isBefore(new Date())) {  // Prevent changing type of past days
      return;
    }

    const isSchoolDay = this.getIsSchoolDay(date);
    if (!isSchoolDay) {
      return;
    }

    this.showDayOptionsModal(date);
  }

  onWcCalendarEventSelection(customEvent: CustomEvent): void {
    const id = customEvent?.detail?.value?.id;
    const date = customEvent?.detail?.value?.start;
    this.showDayContentModal(id, date);
  }

  onWcCalendarEventMouseOver(customEvent: CustomEvent): void {
    const eventData = customEvent?.detail?.value?.eventData;
    const mouseEvent: MouseEvent = customEvent?.detail?.value?.mouseEvent;

    if (!eventData || !mouseEvent) {
      return;
    }

    this.showEdContextMenu(eventData.label, {x: mouseEvent.x, y: mouseEvent.y});
  }

  onWcCalendarEventMouseLeave(customEvent: CustomEvent): void {
    this.closeEdContextMenu();
  }

  onWcCalendarDayButtonMouseOver(customEvent: CustomEvent): void {
    const date = customEvent?.detail?.value?.date;
    const mouseEvent: MouseEvent = customEvent?.detail?.value?.mouseEvent;

    if (!date || !mouseEvent) return;

    let tooltipText = null;

    if (this.getIsSchoolDay(date) && this.isWithinSchoolDays(date)) {
      try {
        tooltipText = `${this.$t('tools.schooldays.schoolDay')}: ${this.getDayTooltip({date, schoolDays: [this.schoolDay]})}`;
      } catch (error) {
        tooltipText = null;
      }
    }

    if (tooltipText) {
      this.showEdContextMenu(tooltipText, {x: mouseEvent.x, y: mouseEvent.y});
    }
  }

  onWcCalendarDayButtonMouseLeave(customEvent: CustomEvent): void {
    this.closeEdContextMenu();
  }

  onWcCalendarEventDragStart(customEvent: CustomEvent): void {
    this.closeEdContextMenu();
  }

  updated() {
    (this.$refs.schoolCalendar as any).createCalendar();
  }

}